import { consoleLoggingIntegration, getIsolationScope, init, isEnabled, } from '@sentry/browser';
import { allowsPerformanceSync, performanceCookieConsentAllowedOnce } from './consent.service';
const LOGGED_IN_TAG = 'logged_in';
const IS_APP_TAG = 'is_app';
const ANONYMOUS_SENTRY_SESSION_STORAGE_KEY = 'utk_sentry_sid';
const ANONYMOUS_SENTRY_USER_ID_PREFIX = 'anon:';
/** Sentry tag for the tab-scoped id. Search issues with `utk_sentry_sid:<value>`. */
export const SENTRY_SESSION_ID_TAG = ANONYMOUS_SENTRY_SESSION_STORAGE_KEY;
let sentrySessionId = null;
const sentryReadyListeners = [];
/**
 * Share of Visually MutationObserver errors to keep.
 * Stored counts are about 1/20th of occurrences (`utk_visually_mo_sample:0.05`).
 * Temporary listen: remove with the Visually script-URL check below once the window ends.
 */
const VISUALLY_MUTATION_OBSERVER_SAMPLE_RATE = 0.05;
const VISUALLY_MUTATION_OBSERVER_SAMPLE_TAG = 'utk_visually_mo_sample';
const VISUALLY_SCRIPT_URL = /visually|vsly\//i;
const MUTATION_OBSERVER_PATTERN = /mutationobserver/i;
/** Third-party script URLs excluded from error reporting. Visually is sampled in `filterSentryErrorEvent`. */
const THIRD_PARTY_DENY_URLS = [
    /elevar/i,
    /getelevar/i,
    /swym/i,
    /bazaarvoice/i,
    // /shopifycloud\/storefront/i, // reevaluate later if we want to block
    /cdn\.shopify\.com\/wpm/i,
    /packs\/js\/shop_now/i,
    /widget/i,
    /uxa\//i,
    /shop_events_listener/i,
    /consent-tracking-api/i,
    /monorail-edge\.shopifysvc\.com/i,
    /boost-pfs-filter/i,
    /awin-shopify-integration/i,
    /webmcp/i,
    // Shopify Shop JS, storefront loaders, and web pixel sandbox workers.
    /shopifycloud\/shop-js/i,
    /shopifycloud\/storefront\/assets\/storefront\/load_feature/i,
    /web-pixels@/i,
    /\/web-pixels\//i,
    /\/cdn\/wpm\//i,
];
/** Known-noise error messages from vendors, browsers, and network conditions. */
const IGNORED_ERROR_PATTERNS = [
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
    'Load failed',
    'AbortError',
    'HTTP Client Error',
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'NetworkError when attempting to fetch resource',
    'Non-Error promise rejection captured',
    /webkit\.messageHandlers/,
    /sendDataToNative/,
    /Java object is gone/,
    /Elevar:/,
    /^swym:/,
    /registerTool.*ModelContext/,
    /could not load https:\/\/apps\.bazaarvoice\.com/,
    /Failed to load https:\/\/assets\.visually\.io/,
    // Loomi (Visually) reads its VISUALLY_IO visit store while WebKit is closing the connection (UNTUCKIT-SHOPIFY-THEME-76).
    /Failed to execute 'transaction' on 'IDBDatabase': The database connection is closing/,
    // Shopify Web Pixels Manager telemetry (UNTUCKIT-SHOPIFY-THEME-2V).
    'Failed to fetch (otlp-http-production.shopifysvc.com)',
    // Shopify consent-tracking-api script load failures (UNTUCKIT-SHOPIFY-THEME-1J).
    /load error: https:\/\/cdn\.shopify\.com\/shopifycloud\/consent-tracking-api\//,
    /load error: https:\/\/[^/]+\/cdn\/shopifycloud\/consent-tracking-api\//,
    // Shopify Shop JS dynamic module load failures (UNTUCKIT-SHOPIFY-THEME-12, -3W).
    /load error: https:\/\/cdn\.shopify\.com\/shopifycloud\/shop-js\//,
    // Shopify web pixel / WPM worker script load failures (UNTUCKIT-SHOPIFY-THEME-5J, -78, -8N).
    /importScripts on 'WorkerGlobalScope'.*\/web-pixels/i,
    /importScripts on 'WorkerGlobalScope'.*\/cdn\/wpm\//i,
    // Third-party app inline scripts not managed in this repo (UNTUCKIT-SHOPIFY-THEME-2W, -7N).
    /extractFilteredSchemaValuesFromJSONLD/,
    /script_src_app_theme_embed/,
    // Safari JSON-LD parser throws on null schema values (UNTUCKIT-SHOPIFY-THEME-2W). See: https://github.com/getsentry/sentry/issues/61469
    /Object\.prototype\.hasOwnProperty\.call\([^,)]+,"telephone"\)/,
    // Facebook Pixel global (`fbq`)
    /fbq/i,
];
const DEFAULT_CONSOLE_LOGGING_LEVELS = ['error'];
/**
 * Key fragments Sentry filtered when `sendDefaultPii` was off.
 * v11 collects these categories unless `dataCollection` sets them explicitly.
 */
const RESTRICTED_DATA_KEY_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user'];
/** Matches the SDK's previous `sendDefaultPii: false` collection. Explicit `setUser()` is unaffected. */
const DATA_COLLECTION = {
    userInfo: false,
    cookies: { deny: [...RESTRICTED_DATA_KEY_DENYLIST] },
    httpHeaders: {
        request: { deny: [...RESTRICTED_DATA_KEY_DENYLIST] },
        response: { deny: [...RESTRICTED_DATA_KEY_DENYLIST] },
    },
    httpBodies: [],
    urlQueryParams: { deny: [...RESTRICTED_DATA_KEY_DENYLIST] },
    graphQL: { document: true, variables: true },
    genAI: { inputs: false, outputs: false },
    databaseQueryData: false,
    stackFrameVariables: true,
    frameContextLines: 7,
};
function isLocalHost() {
    const hostname = window.location.hostname;
    return hostname === 'localhost' || hostname === '127.0.0.1';
}
function isSentryDebugEnabled() {
    return new URLSearchParams(window.location.search).has('sentry_debug');
}
function parseConsoleLoggingLevels(levels) {
    if (!levels?.trim()) {
        return DEFAULT_CONSOLE_LOGGING_LEVELS;
    }
    return levels.split(',').map((level) => level.trim()).filter(Boolean);
}
function frameUrl(frame) {
    return frame.filename || frame.abs_path || '';
}
function throwingFrameUrl(frames) {
    for (let index = frames.length - 1; index >= 0; index -= 1) {
        const url = frameUrl(frames[index]);
        if (url) {
            return url;
        }
    }
    return '';
}
function mentionsMutationObserver(event, functionNames) {
    const exception = event.exception?.values?.[0];
    const text = [exception?.type, exception?.value, event.message, functionNames].filter(Boolean).join(' ');
    return MUTATION_OBSERVER_PATTERN.test(text);
}
/**
 * Drops third-party stacks. Visually MutationObserver errors are the exception:
 * `denyUrls` would drop them before this runs, so Visually is omitted there and
 * kept here at {@link VISUALLY_MUTATION_OBSERVER_SAMPLE_RATE}.
 */
export function filterSentryErrorEvent(event, random = Math.random) {
    const frames = event.exception?.values?.[0]?.stacktrace?.frames;
    if (!frames?.length) {
        return event;
    }
    const functionNames = frames.map((frame) => frame.function || '').join(' ');
    const fromVisually = VISUALLY_SCRIPT_URL.test(throwingFrameUrl(frames));
    if (fromVisually) {
        if (!mentionsMutationObserver(event, functionNames) || random() >= VISUALLY_MUTATION_OBSERVER_SAMPLE_RATE) {
            return null;
        }
        event.tags = {
            ...event.tags,
            [VISUALLY_MUTATION_OBSERVER_SAMPLE_TAG]: String(VISUALLY_MUTATION_OBSERVER_SAMPLE_RATE),
        };
        return event;
    }
    const hasInAppFrame = frames.some((frame) => frame.in_app === true);
    return hasInAppFrame ? event : null;
}
function beforeSend(event) {
    return filterSentryErrorEvent(event);
}
function buildBaseOptions() {
    const sentryConfig = window.UNTUCKitConfig?.sentry;
    return {
        dsn: sentryConfig?.dsn,
        environment: sentryConfig?.environment,
        ignoreErrors: IGNORED_ERROR_PATTERNS,
        denyUrls: THIRD_PARTY_DENY_URLS,
        beforeSend,
        debug: isSentryDebugEnabled(),
        dataCollection: DATA_COLLECTION,
        integrations: [
            consoleLoggingIntegration({
                levels: parseConsoleLoggingLevels(sentryConfig?.consoleLoggingLevels),
            }),
        ],
    };
}
function getOrCreateAnonymousSentrySessionId() {
    try {
        const existing = sessionStorage.getItem(ANONYMOUS_SENTRY_SESSION_STORAGE_KEY);
        if (existing) {
            sentrySessionId = existing;
            return existing;
        }
        const sessionId = crypto.randomUUID();
        sessionStorage.setItem(ANONYMOUS_SENTRY_SESSION_STORAGE_KEY, sessionId);
        sentrySessionId = sessionId;
        return sessionId;
    }
    catch {
        if (!sentrySessionId) {
            sentrySessionId = crypto.randomUUID();
        }
        return sentrySessionId;
    }
}
/**
 * Tab-scoped id for the current Sentry session.
 * Returns null until Sentry has started, so the id is not exposed when performance consent is off.
 */
export function getSentrySessionId() {
    if (!isEnabled()) {
        return null;
    }
    return sentrySessionId;
}
/** Runs `listener` once Sentry has started. If it never starts, `listener` is not called. */
export function whenSentryReady(listener) {
    if (isEnabled()) {
        listener();
        return;
    }
    sentryReadyListeners.push(listener);
}
function notifySentryReady() {
    const listeners = sentryReadyListeners.splice(0);
    listeners.forEach((listener) => {
        listener();
    });
}
/** Applies Sentry user id and segmentation tags. Call after `init()`. */
export function applySentryUserContext() {
    const sentryConfig = window.UNTUCKitConfig?.sentry;
    const scope = getIsolationScope();
    const customerId = sentryConfig?.customerId;
    const sessionId = getOrCreateAnonymousSentrySessionId();
    scope.setTag(SENTRY_SESSION_ID_TAG, sessionId);
    if (customerId != null) {
        scope.setUser({ id: String(customerId) });
        scope.setTag(LOGGED_IN_TAG, 'true');
    }
    else {
        scope.setUser({ id: `${ANONYMOUS_SENTRY_USER_ID_PREFIX}${sessionId}` });
        scope.setTag(LOGGED_IN_TAG, 'false');
    }
    if (window.UNTUCKitConfig?.thirdPartyToggles?.isApp) {
        scope.setTag(IS_APP_TAG, 'true');
    }
}
function initSentry() {
    if (isEnabled()) {
        return;
    }
    init(buildBaseOptions());
    applySentryUserContext();
    notifySentryReady();
}
/**
 * Initializes Sentry with console logging instrumentation.
 * Traffic waits for PieEye Performance consent (pieeye-category-performance).
 */
export function setupSentry() {
    const sentryConfig = window.UNTUCKitConfig?.sentry;
    if (!sentryConfig?.enable || isLocalHost()) {
        return;
    }
    if (allowsPerformanceSync()) {
        initSentry();
        return;
    }
    performanceCookieConsentAllowedOnce.subscribe(() => {
        initSentry();
    });
}
